import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { useI18next } from "gatsby-plugin-react-i18next";
import RichtextRenderer from "../RichtextRenderer";
import Input from "../basic/Input";
import Button from "../basic/Button";
import Container from "../Container";

import MailchimpSubscribe from "react-mailchimp-subscribe";

const SubscribeForm = ({
  status,
  message,
  onValidated,
  formData,
  title,
  nameInputLabel,
  nameInputPlaceholder,
  nameErrorMessage,
  emailInputLabel,
  emailInputPlaceholder,
  emailErrorMessage,
  requiredErrorMessage,
  submitButtonLabel,
  text,
}) => {
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().max(50, nameErrorMessage).required(requiredErrorMessage),
      email: Yup.string().email(emailErrorMessage).required(requiredErrorMessage),
    }),
    onSubmit: async (values, { resetForm }) => {
      onValidated({
        EMAIL: values.email,
        MERGE1: values.name,
      });

      resetForm();
    },
  });

  if (status === "success") {
    return (
      <Container outerContainerClassName="flex items-center justify-center min-h-142px">
        <h2 className="text-center !mb-0">{message}</h2>
      </Container>
    );
  }

  return (
    <form id="mailing-list-form" className="md:py-32px" onSubmit={formik.handleSubmit}>
      <div className="flex flex-col justify-between w-full">
        <h2 className="w-full text-blue capitalize">{title}</h2>
        <RichtextRenderer
          data={text}
          config={{
            p: "text-blue whitespace-pre-wrap",
          }}
        />
        <div className="w-full flex flex-col items-center my-24px md:mt-16px md:mb-32px">
          <div className="w-full mb-16px">
            <div className="relative bg-white border border-[#CFD3D4] rounded-8px px-16px py-8px">
              <div className="sidebar-text !text-[0.625rem] !leading-[136%] my-4px text-nav-green">
                {nameInputLabel}
              </div>
              <Input
                id="name"
                name="name"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                title={formik.errors.name}
                value={formik.values.name}
                placeholder={nameInputPlaceholder}
                className="bg-white placeholder:text-[#ABAFB1] shadow-none !p-0 !text-18px placeholder:italic"
              />
            </div>
          </div>
          <div className="relative w-full">
            <div className="relative bg-white border border-[#CFD3D4] rounded-8px px-16px py-8px">
              <div className="sidebar-text !text-[0.625rem] !leading-[136%] my-4px text-nav-green">
                {emailInputLabel}
              </div>
              <Input
                id="email"
                name="email"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                title={formik.errors.email}
                value={formik.values.email}
                placeholder={emailInputPlaceholder}
                className="bg-white placeholder:text-[#ABAFB1] shadow-none !p-0 !text-18px placeholder:italic"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-0 md:gap-24px">
          <Button
            disabled={formik.isSubmitting || Object.keys(formik.errors).length !== 0}
            px="px-42px md:px-16px"
            type="submit"
            variant="beige"
            className="w-fit !mt-0 text-left disabled:!opacity-[0.7] justify-between md:justify-center"
            arrowClassName="!mt-0"
            buttonClassName="!py-0"
            arrow
          >
            {submitButtonLabel}
          </Button>
          {((formik.errors.name && formik.touched.name) || (formik.errors.email && formik.touched.email)) && (
            <div className="mt-32px md:mt-0 w-fit text-red-600 md:order-first">
              {formik.errors.name || formik.errors.email}
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

const MailingListForm = ({
  formData,
  title,
  nameInputLabel,
  nameInputPlaceholder,
  nameErrorMessage,
  emailInputLabel,
  emailInputPlaceholder,
  emailErrorMessage,
  requiredErrorMessage,
  submitButtonLabel,
  text,
}) => {
  const postUrl =
    "https://gminingventures.us20.list-manage.com/subscribe/post?u=72b5e92686e1a539c139c15d3&id=0b2ac89059";

  return (
    <MailchimpSubscribe
      url={postUrl}
      render={({ subscribe, status, message }) => (
        <SubscribeForm
          status={status}
          message={message}
          onValidated={(formData) => subscribe(formData)}
          title={title}
          formData={formData}
          nameInputLabel={nameInputLabel}
          nameInputPlaceholder={nameInputPlaceholder}
          nameErrorMessage={nameErrorMessage}
          emailInputLabel={emailInputLabel}
          emailInputPlaceholder={emailInputPlaceholder}
          emailErrorMessage={emailErrorMessage}
          requiredErrorMessage={requiredErrorMessage}
          submitButtonLabel={submitButtonLabel}
          text={text}
        />
      )}
    />
  );
};

const getFormData = (data, language) => {
  const formData = language === "fr" ? data.nodes[1] : data.nodes[0];

  return {
    title: formData.mailingListFormTitle,
    text: formData.mailingListFormText,
    image: formData.mailingListFormImage,
    backgroundImage: formData.mailingListFormBackgroundImage,
    nameInputLabel: formData.mailingListFormNameInputLabel,
    nameInputPlaceholder: formData.mailingListFormNameInputPlaceholder,
    nameErrorMessage: formData.mailingListFormNameErrorMessage,
    emailInputLabel: formData.mailingListFormEmailInputLabel,
    emailInputPlaceholder: formData.mailingListFormEmailInputPlaceholder,
    emailErrorMessage: formData.mailingListFormEmailErrorMessage,
    requiredErrorMessage: formData.mailingListFormRequiredErrorMessage,
    submitButtonLabel: formData.mailingListFormSubmitButtonLabel,
  };
};

const MailingListSection = () => {
  const { data } = useStaticQuery(graphql`
    query MailingSectionQuery {
      data: allContentfulHeaderAndFooter {
        nodes {
          mailingListFormTitle
          mailingListFormText {
            raw
          }
          mailingListFormNameInputLabel
          mailingListFormNameInputPlaceholder
          mailingListFormNameErrorMessage
          mailingListFormEmailInputLabel
          mailingListFormEmailInputPlaceholder
          mailingListFormEmailErrorMessage
          mailingListFormRequiredErrorMessage
          mailingListFormSubmitButtonLabel
          mailingListFormImage {
            title
            gatsbyImageData(placeholder: NONE)
          }
          mailingListFormBackgroundImage {
            title
            gatsbyImageData
          }
          socialsTitle
          youTubeLink
          twitterLink
          linkedInLink
          facebookLink
          links {
            label
            url
            subItems {
              label
              url
            }
          }
          copyright
          node_locale
        }
      }
    }
  `);
  const { language } = useI18next();

  const formProps = getFormData(data, language);

  return (
    <Container outerContainerClassName="!py-46px">
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-64px p-32px md:py-0 md:px-32px overflow-hidden rounded-8px bg-blue/10">
        <GatsbyImage
          image={formProps.backgroundImage.gatsbyImageData}
          alt={formProps.backgroundImage.title}
          objectFit="cover"
          quality={100}
          className="!absolute w-full h-full opacity-[0.05] z-0"
        />
        <div className="relative flex items-center text-text-blue z-10">
          <MailingListForm {...formProps} />
        </div>
        <div className="relative hidden md:block w-full h-full z-10">
          <div className="absolute w-full h-full">
            <div className="w-full h-full py-32px">
              <GatsbyImage
                image={formProps.image.gatsbyImageData}
                alt={formProps.image.title}
                objectFit="contain"
                quality={100}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default MailingListSection;
